import { useState } from 'react'
import type { Tier } from '../../lib/api'
import {
  adminApi,
  type AdminBroadcastOut,
  type AdminEmailTest,
  type AdminMirrorProbe,
} from '../../lib/adminApi'
import { APP_VERSION, buildLabel } from '../../lib/version'
import {
  DataTable,
  ErrorNote,
  Loading,
  Pill,
  Section,
  dateTime,
  fmt,
  fmtBytes,
  useAsync,
} from './parts'

export function System({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const { data, error } = useAsync(() => adminApi.system(), 'system')
  const [probes, setProbes] = useState<AdminMirrorProbe[] | null>(null)
  const [probing, setProbing] = useState(false)
  const [probeError, setProbeError] = useState<string | null>(null)

  if (error) return <ErrorNote message={error} />
  if (!data) return <Loading what="system status" />

  return (
    <>
      <Section title="Build">
        <dl className="staff-facts staff-facts--wide">
          <div>
            <dt>Client</dt>
            <dd>
              v{APP_VERSION} · {buildLabel()}
            </dd>
          </div>
          <div>
            <dt>API</dt>
            <dd>v{data.api_version}</dd>
          </div>
          <div>
            <dt>Server time</dt>
            <dd>{dateTime(data.server_time)}</dd>
          </div>
          <div>
            <dt>Schema revision</dt>
            <dd>
              <code className="staff-code">{data.alembic_revision ?? 'unknown'}</code>
            </dd>
          </div>
        </dl>
      </Section>

      <Section title="Database">
        <dl className="staff-facts staff-facts--wide">
          <div>
            <dt>Name</dt>
            <dd>{data.database}</dd>
          </div>
          <div>
            <dt>Size</dt>
            <dd>{data.database_size}</dd>
          </div>
          <div>
            <dt>Connections</dt>
            <dd>{fmt(data.connections)}</dd>
          </div>
          <div>
            <dt>Postgres</dt>
            <dd>{data.postgres_version}</dd>
          </div>
          <div>
            <dt>PostGIS</dt>
            <dd>{data.postgis_version ?? 'not installed'}</dd>
          </div>
        </dl>

        <DataTable
          rows={data.tables}
          rowId={(t) => t.name}
          empty="No table statistics."
          columns={[
            { key: 'n', head: 'Table', render: (t) => t.name },
            { key: 'r', head: 'Live rows', numeric: true, render: (t) => fmt(t.live_rows) },
            { key: 'b', head: 'On disk', numeric: true, render: (t) => fmtBytes(t.bytes) },
          ]}
        />
      </Section>

      <Section title="Configuration">
        <dl className="staff-facts staff-facts--wide">
          <div>
            <dt>Web Push</dt>
            <dd>
              {data.push_configured ? (
                <Pill tone="green">configured</Pill>
              ) : (
                <Pill tone="red">no VAPID keys</Pill>
              )}
            </dd>
          </div>
          <div>
            <dt>VAPID subject</dt>
            <dd>{data.vapid_subject}</dd>
          </div>
          <div>
            <dt>Free tier cap</dt>
            <dd>{data.free_tier_reminder_limit} active reminders</dd>
          </div>
          <div>
            <dt>Geofence clear factor</dt>
            <dd>{data.geofence_clear_factor}× radius</dd>
          </div>
          <div>
            <dt>Store cache TTL</dt>
            <dd>{Math.round(data.store_cache_ttl_seconds / 86400)} days</dd>
          </div>
          <div>
            <dt>CORS origins</dt>
            <dd>{data.cors_origins.join(', ') || 'none'}</dd>
          </div>
        </dl>
        {!data.push_configured && (
          <p className="banner">
            Household Ping and any broadcast are silently no-ops without VAPID keys. Generate a
            pair with <code className="staff-code">python -m app.cli vapid</code> and set{' '}
            <code className="staff-code">NUDGE_VAPID_*</code>.
          </p>
        )}
      </Section>

      <Section
        title="Overpass mirrors"
        action={
          <button
            className="btn btn--ghost btn--sm"
            disabled={probing}
            onClick={async () => {
              setProbing(true)
              setProbeError(null)
              try {
                setProbes(await adminApi.overpassProbe())
              } catch (e) {
                setProbeError(e instanceof Error ? e.message : String(e))
              } finally {
                setProbing(false)
              }
            }}
          >
            {probing ? 'Probing…' : 'Probe now'}
          </button>
        }
      >
        <p className="hint">
          Store lookups never run inside a request — they are warmed in the background, because a
          stalled mirror once made position reports hang for over a minute. This probe is the one
          deliberate exception, and it gives up after 10 seconds.
        </p>
        {probeError && <ErrorNote message={probeError} />}
        {probes ? (
          <DataTable
            rows={probes}
            rowId={(p) => p.url}
            empty="No mirrors configured."
            columns={[
              { key: 'u', head: 'Mirror', render: (p) => <code className="staff-code">{p.url}</code> },
              {
                key: 's',
                head: 'Result',
                render: (p) =>
                  p.ok ? <Pill tone="green">ok</Pill> : <Pill tone="red">{p.status ?? 'failed'}</Pill>,
              },
              { key: 'l', head: 'Latency', numeric: true, render: (p) => `${fmt(p.latency_ms)} ms` },
              { key: 'e', head: 'Detail', render: (p) => p.error ?? '—' },
            ]}
          />
        ) : (
          <ul className="staff-list">
            {data.overpass_mirrors.map((m) => (
              <li key={m}>
                <code className="staff-code">{m}</code>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {isSuperAdmin && <EmailTest />}

      {isSuperAdmin && <Broadcast pushConfigured={data.push_configured} />}
    </>
  )
}

function EmailTest() {
  const [address, setAddress] = useState('')
  const [result, setResult] = useState<AdminEmailTest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function send() {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult(await adminApi.emailTest(address.trim()))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Section title="Email">
      <p className="hint">
        Sends a real message through the configured relay and waits for the answer, so a failure
        names the relay it failed against. This box cannot send its own mail — outbound port 25 is
        blocked and the SPF record authorises Google Workspace — so everything goes through an
        authenticated relay on 587.
      </p>

      {result && (
        <dl className="staff-facts staff-facts--wide">
          <div>
            <dt>Relay</dt>
            <dd>
              {result.smtp_host ? (
                <code className="staff-code">
                  {result.smtp_host}:{result.smtp_port}
                </code>
              ) : (
                <Pill tone="amber">not configured</Pill>
              )}
            </dd>
          </div>
          <div>
            <dt>Username</dt>
            <dd>{result.smtp_user || '—'}</dd>
          </div>
          <div>
            <dt>From</dt>
            <dd>{result.email_from}</dd>
          </div>
          <div>
            {/* The setting that fails silently: mail delivers, links are dead. */}
            <dt>Links point at</dt>
            <dd>
              <code className="staff-code">{result.app_base_url}</code>
            </dd>
          </div>
        </dl>
      )}

      <div className="staff-form staff-form--row">
        <input
          className="staff-input"
          type="email"
          placeholder="where to send it"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && address.trim() && !busy && void send()}
        />
        <button className="btn btn--sm" disabled={busy || !address.trim()} onClick={send}>
          {busy ? 'Sending…' : 'Send test'}
        </button>
      </div>

      {error && <ErrorNote message={error} />}
      {result && (
        <p className="staff-note">
          <Pill tone={result.sent ? 'green' : result.configured ? 'red' : 'amber'}>
            {result.sent ? 'sent' : result.configured ? 'failed' : 'logged only'}
          </Pill>{' '}
          {result.detail}
        </p>
      )}
    </Section>
  )
}

function Broadcast({ pushConfigured }: { pushConfigured: boolean }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [url, setUrl] = useState('/')
  const [audience, setAudience] = useState<'all' | 'tier' | 'admins'>('all')
  const [tier, setTier] = useState<Tier>('free')
  const [result, setResult] = useState<AdminBroadcastOut | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function send(dry: boolean) {
    setBusy(true)
    setError(null)
    try {
      setResult(
        await adminApi.broadcast({
          title,
          body,
          url,
          audience,
          tier: audience === 'tier' ? tier : undefined,
          dry_run: dry,
        }),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const ready = title.trim().length > 0 && body.trim().length > 0

  return (
    <Section title="Broadcast">
      <p className="hint">
        Sends a Web Push to every matching account. Suspended accounts are never included.
        Preview first — it returns the same audience count a live send would use.
      </p>
      {!pushConfigured && (
        <p className="banner">Push is not configured, so a live send would reach nobody.</p>
      )}

      <div className="staff-form">
        <input
          className="staff-input"
          placeholder="Notification title"
          maxLength={120}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="staff-input staff-input--area"
          rows={2}
          maxLength={400}
          placeholder="Body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="staff-filters">
          <label className="staff-filter">
            <span>Opens</span>
            <input className="staff-input" value={url} onChange={(e) => setUrl(e.target.value)} />
          </label>
          <label className="staff-filter">
            <span>Audience</span>
            <select
              className="staff-input"
              value={audience}
              onChange={(e) => setAudience(e.target.value as 'all' | 'tier' | 'admins')}
            >
              <option value="all">everyone</option>
              <option value="tier">one plan</option>
              <option value="admins">staff only</option>
            </select>
          </label>
          {audience === 'tier' && (
            <label className="staff-filter">
              <span>Plan</span>
              <select
                className="staff-input"
                value={tier}
                onChange={(e) => setTier(e.target.value as Tier)}
              >
                <option value="free">free</option>
                <option value="pro">pro</option>
                <option value="family">family</option>
              </select>
            </label>
          )}
        </div>
        <div className="staff-actions">
          <button
            className="btn btn--ghost btn--sm"
            disabled={busy || !ready}
            onClick={() => void send(true)}
          >
            Preview audience
          </button>
          <button
            className="btn btn--ghost btn--sm staff-danger"
            disabled={busy || !ready || !result || result.dry_run === false}
            title={result ? undefined : 'Preview the audience first'}
            onClick={() => void send(false)}
          >
            Send for real
          </button>
        </div>
      </div>

      {error && <ErrorNote message={error} />}
      {result && (
        <p className="banner" role="status">
          {result.dry_run
            ? `Would reach ${fmt(result.targeted_users)} account(s) across ${fmt(
                result.subscriptions,
              )} device(s). Nothing was sent.`
            : `Sent to ${fmt(result.delivered)} device(s) across ${fmt(
                result.targeted_users,
              )} account(s). ${fmt(result.pruned)} dead subscription(s) pruned.`}
        </p>
      )}
    </Section>
  )
}
